import React from 'react';
import logo from '../assets/logo.svg';

const styles = {
  page: { maxWidth: '800px', margin: '0 auto', padding: '48px 24px', fontFamily: "'Segoe UI', sans-serif", color: '#111827' },
  title: { fontSize: '32px', fontWeight: '800', color: '#111827', marginBottom: '8px' },
  updated: { fontSize: '13px', color: '#6b7280', marginBottom: '40px' },
  section: { marginBottom: '32px' },
  h2: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  p: { fontSize: '14px', color: '#374151', lineHeight: '1.8', marginBottom: '12px' },
  ul: { fontSize: '14px', color: '#374151', lineHeight: '1.8', paddingLeft: '20px', marginBottom: '12px' },
  li: { marginBottom: '6px' },
  divider: { height: '1px', backgroundColor: '#e5e7eb', margin: '32px 0' },
  contact: { backgroundColor: '#f9fafb', borderRadius: '10px', padding: '20px 24px', border: '1px solid #e5e7eb' },
  table: { width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '13px' },
  th: { backgroundColor: '#f3f4f6', padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#374151', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f3f4f6', color: '#374151', verticalAlign: 'top' },
};

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <img src={logo} alt="V2VLab" style={{ height: '32px', marginBottom: '32px', display: 'block' }} />
      <div style={styles.title}>PRIVACY POLICY</div>
      <div style={styles.updated}>Last updated: July 4, 2026</div>

      <div style={styles.section}>
        <div style={styles.h2}>1. INTRODUCTION</div>
        <p style={styles.p}>Converstation AI LLC ("we," "us," or "our") operates V2VLab ("the Platform"). This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use V2VLab.</p>
        <p style={styles.p}>BY USING V2VLAB, YOU CONSENT TO THE DATA PRACTICES DESCRIBED IN THIS PRIVACY POLICY. If you do not agree, please do not use the Platform.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>2. INFORMATION WE COLLECT</div>
        <p style={styles.p}><strong>ACCOUNT INFORMATION:</strong> When you register, we collect your name, email address, organization, professional role, and password (stored in encrypted form).</p>
        <p style={styles.p}><strong>USAGE DATA:</strong> We collect information about how you use V2VLab, including simulations run, agents and scenarios created, practice sessions completed, features accessed, and usage metrics (daily/monthly simulation counts, TTS character usage).</p>
        <p style={styles.p}><strong>SIMULATION CONTENT:</strong> We store simulation transcripts, audio files, evaluation results, perturbation configurations, and corpus data you generate on the Platform.</p>
        <p style={styles.p}><strong>PAYMENT INFORMATION:</strong> Payment processing is handled by Stripe. We do not store your full credit card number. We retain billing records including subscription status, payment history, and Stripe customer IDs.</p>
        <p style={styles.p}><strong>TECHNICAL DATA:</strong> We automatically collect IP addresses, browser type, device information, and access logs for security and performance monitoring.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>3. HOW WE USE YOUR INFORMATION</div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Data Used</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Provide and operate V2VLab', 'Account info, simulation content, usage data'],
              ['Process payments and billing', 'Payment info, subscription status'],
              ['Enforce usage limits and plan features', 'Usage data, account info'],
              ['Send transactional emails', 'Email address'],
              ['Improve platform performance', 'Technical data, usage patterns'],
              ['Respond to support requests', 'Account info, usage data'],
              ['Comply with legal obligations', 'All categories as required'],
              ['Detect and prevent fraud or abuse', 'Technical data, usage data'],
            ].map(([purpose, data], i) => (
              <tr key={i}>
                <td style={styles.td}>{purpose}</td>
                <td style={styles.td}>{data}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={styles.p}>WE DO NOT SELL YOUR PERSONAL INFORMATION TO THIRD PARTIES. WE DO NOT USE YOUR SIMULATION CONTENT FOR AI MODEL TRAINING WITHOUT YOUR EXPLICIT CONSENT.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>4. THIRD-PARTY SERVICE PROVIDERS</div>
        <p style={styles.p}>We share data with the following third-party providers solely to operate V2VLab:</p>
        <ul style={styles.ul}>
          <li style={styles.li}><strong>Firebase (Google):</strong> Authentication, database, and file storage</li>
          <li style={styles.li}><strong>Google Cloud Run:</strong> Backend hosting and computation</li>
          <li style={styles.li}><strong>Stripe:</strong> Payment processing and subscription management</li>
          <li style={styles.li}><strong>Anthropic, OpenAI, Google:</strong> LLM-powered conversation generation (your prompt content is sent to your selected provider)</li>
          <li style={styles.li}><strong>ElevenLabs:</strong> Text-to-speech synthesis (text content is sent for audio generation)</li>
          <li style={styles.li}><strong>Deepgram:</strong> Speech-to-text transcription (audio is sent for transcription)</li>
          <li style={styles.li}><strong>Resend:</strong> Transactional email delivery</li>
        </ul>
        <p style={styles.p}>Each provider operates under its own privacy policy. We encourage you to review them. We enter into data processing agreements with providers where required by applicable law.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>5. DATA RETENTION</div>
        <p style={styles.p}><strong>STANDARD PLAN:</strong> Simulation history is retained for 7 days. Account data is retained for the duration of your account.</p>
        <p style={styles.p}><strong>PROFESSIONAL AND ENTERPRISE PLANS:</strong> Simulation history and content are retained indefinitely while your account is active.</p>
        <p style={styles.p}>Upon account deletion, we will delete your personal data within 30 days, except where retention is required by law or legitimate business purposes (e.g., billing records).</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>6. DATA SECURITY</div>
        <p style={styles.p}>We implement industry-standard security measures to protect your data, including:</p>
        <ul style={styles.ul}>
          <li style={styles.li}>Encrypted data transmission (TLS/HTTPS)</li>
          <li style={styles.li}>Encrypted storage via Firebase</li>
          <li style={styles.li}>Access controls and authentication</li>
          <li style={styles.li}>Regular security monitoring</li>
        </ul>
        <p style={styles.p}>HOWEVER, NO METHOD OF TRANSMISSION OR STORAGE IS 100% SECURE. WE CANNOT GUARANTEE ABSOLUTE SECURITY OF YOUR DATA.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>7. YOUR RIGHTS</div>
        <p style={styles.p}>Depending on your location, you may have the following rights regarding your personal data:</p>
        <ul style={styles.ul}>
          <li style={styles.li}><strong>ACCESS:</strong> Request a copy of the personal data we hold about you</li>
          <li style={styles.li}><strong>CORRECTION:</strong> Request correction of inaccurate data</li>
          <li style={styles.li}><strong>DELETION:</strong> Request deletion of your personal data</li>
          <li style={styles.li}><strong>PORTABILITY:</strong> Request export of your data in a machine-readable format</li>
          <li style={styles.li}><strong>OBJECTION:</strong> Object to certain processing of your data</li>
          <li style={styles.li}><strong>WITHDRAWAL OF CONSENT:</strong> Withdraw consent where processing is based on consent</li>
        </ul>
        <p style={styles.p}>To exercise these rights, contact us at info@converstation.ai. We will respond within 30 days.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>8. COOKIES AND TRACKING</div>
        <p style={styles.p}>V2VLab uses essential cookies and browser storage (localStorage, sessionStorage) to maintain your login session and user preferences. We do not use advertising or tracking cookies. We do not share tracking data with advertising networks.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>9. CHILDREN'S PRIVACY</div>
        <p style={styles.p}>V2VLAB IS NOT INTENDED FOR USE BY INDIVIDUALS UNDER THE AGE OF 18. We do not knowingly collect personal information from minors. If you believe we have collected data from a minor, contact us immediately at info@converstation.ai and we will delete it promptly.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>10. INTERNATIONAL DATA TRANSFERS</div>
        <p style={styles.p}>V2VLab is operated from the United States. If you access the Platform from outside the United States, your data will be transferred to and processed in the United States. By using V2VLab, you consent to this transfer.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.section}>
        <div style={styles.h2}>11. CHANGES TO THIS POLICY</div>
        <p style={styles.p}>We may update this Privacy Policy from time to time. We will notify you of material changes via email or a prominent notice on the Platform. YOUR CONTINUED USE OF V2VLAB AFTER CHANGES TAKE EFFECT CONSTITUTES YOUR ACCEPTANCE OF THE REVISED POLICY.</p>
      </div>

      <div style={styles.divider} />

      <div style={styles.contact}>
        <div style={styles.h2}>CONTACT US</div>
        <p style={styles.p}>For privacy-related questions or to exercise your rights, contact us at:</p>
        <p style={styles.p}><strong>Converstation AI LLC</strong><br />
        Email: <a href="mailto:info@converstation.ai" style={{ color: '#0052CC' }}>info@converstation.ai</a><br />
        Website: <a href="https://converstation.ai" target="_blank" rel="noopener noreferrer" style={{ color: '#0052CC' }}>converstation.ai</a></p>
      </div>
    </div>
  );
}
